'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "obsidiana" (monocromo editorial): omakase / chef's table.
 * El ÚNICO hero asimétrico del catálogo: contenido anclado abajo-izquierda,
 * nombre gigante en Syne a ancho de columna y la media SIEMPRE en blanco y
 * negro (la firma del tema). El overlay del token trae doble velo (superior
 * para el nav + inferior para el texto). CTA invertido blanco/negro — no usa
 * el acento, que en este tema es grafito para los componentes compartidos.
 * Lee tokens del tema (--ms-*).
 */
export function HeroObsidiana({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)
  const monoFilter = 'grayscale(1) contrast(1.08) brightness(0.92)'

  // Syne 800 avanza hasta ~1.2em por cap (K/R anchas + aire): el tamaño se deriva de la palabra más
  // larga del nombre para que NUNCA se parta — ni en 375px ni en desktop.
  // ~86vw usables en pantallas chicas; el contenedor (max-w-5xl - padding)
  // acota el tope absoluto. break-words en el h1 queda solo como red.
  const longestWord = Math.max(...businessName.trim().split(/\s+/).map((w) => w.length), 1)
  const nameFontSize = `clamp(20px, ${(86 / (longestWord * 1.2)).toFixed(2)}vw, ${Math.min(
    140,
    Math.round(976 / (longestWord * 1.2)),
  )}px)`

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre SIEMPRE en B/N — firma del tema */}
      {heroVideoUrl ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: monoFilter }}
          src={heroVideoUrl}
        />
      ) : heroImageUrl ? (
        <Image
          src={heroImageUrl}
          alt={businessName}
          fill
          priority
          sizes="100vw"
          className="object-cover"
          style={{ filter: monoFilter }}
        />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Contenido abajo-izquierda */}
      <div
        className="relative z-20 mx-auto flex w-full max-w-5xl flex-col items-start justify-end px-6 pb-16 pt-32 text-left"
        style={{ minHeight: 'min(700px, 90vh)' }}
      >
        {/* Regla corta blanca — apertura editorial */}
        <span className="mb-6 block h-[2px] w-[52px]" style={{ background: 'var(--ms-text)' }} aria-hidden />

        {(city || statusLabel) && (
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.34em]" style={{ color: 'var(--ms-text-muted)' }}>
            {city}
            {city && statusLabel && <span aria-hidden> / </span>}
            {statusLabel && <span style={{ color: statusLabel.tone === 'open' ? 'var(--ms-text)' : undefined }}>{statusLabel.text}</span>}
          </span>
        )}

        <h1
          className="mt-4 w-full break-words uppercase leading-[0.98] tracking-[-0.015em]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            fontWeight: 800,
            fontSize: nameFontSize,
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 6px 44px rgba(0,0,0,0.6)' : undefined,
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-4 max-w-md text-[15.5px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>/</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: pill blanco invertido + outline hairline */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center rounded-full px-9 py-4 text-[12px] font-bold uppercase tracking-[0.2em] transition hover:brightness-90 active:scale-[0.98]"
            style={{
              background: 'var(--ms-text)',
              color: 'var(--ms-bg)',
              boxShadow: '0 20px 46px -18px rgba(0,0,0,0.9)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-7 py-4 text-[11.5px] font-semibold uppercase tracking-[0.2em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                border: '1px solid rgba(255,255,255,0.28)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
